import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Sparkles, X, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STORAGE_KEY = "vixera:exit-intent";
const COOLDOWN_MS = 24 * 3600 * 1000;    // one shot per day per browser
const MIN_ENGAGEMENT_MS = 15_000;         // don't fire until visitor has read something

/**
 * Exit-intent capture modal.
 *
 * Triggers when a not-logged-in visitor:
 *  - moves the mouse off the top of the viewport (desktop)
 *  - or scrolls up quickly after scrolling down (mobile)
 * ...and has been on the site at least MIN_ENGAGEMENT_MS.
 *
 * Rewards them with a R100-off single-use voucher (backed by /api/vouchers/exit-intent).
 * Rate-limited server-side per IP + client-side per browser.
 */
export default function ExitIntentModal() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const mountedAt = useRef(Date.now());
  const armed = useRef(false);
  const lastScrollY = useRef(0);
  const lastScrollT = useRef(0);

  // Reset "seen" flag on route change so we can still catch it later if user hasn't triggered.
  useEffect(() => {
    lastScrollY.current = 0;
    lastScrollT.current = 0;
  }, [location.pathname]);

  useEffect(() => {
    // Never show to logged-in users
    if (user) return;
    // Suppress on auth/admin/checkout flows
    if (/^\/(admin|login|register|choose-username|auth\/|payment\/)/.test(location.pathname)) return;

    // Once-per-day per browser
    try {
      const last = Number(localStorage.getItem(STORAGE_KEY) || 0);
      if (Date.now() - last < COOLDOWN_MS) return;
    } catch {}

    armed.current = true;

    const trigger = () => {
      if (!armed.current) return;
      if (Date.now() - mountedAt.current < MIN_ENGAGEMENT_MS) return;
      armed.current = false;
      setOpen(true);
      try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
    };

    // Desktop: mouse leaves via the TOP of the viewport
    const onMouseOut = (e) => {
      if (e.relatedTarget) return;
      if (e.clientY <= 0) trigger();
    };

    // Mobile: fast upward scroll flick after scrolling well down the page
    const onScroll = () => {
      const y = window.scrollY;
      const t = Date.now();
      const dy = y - lastScrollY.current;
      const dt = t - lastScrollT.current;
      // Only consider once the visitor is at least 40% down the page
      if (y > window.innerHeight * 0.4 && dy < -80 && dt < 500) trigger();
      lastScrollY.current = y;
      lastScrollT.current = t;
    };

    document.addEventListener("mouseout", onMouseOut);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScroll);
    };
  }, [user, location.pathname]);

  const claim = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/vouchers/exit-intent");
      setVoucher(data);
    } catch (err) {
      // If we hit the 12h rate limit, clear our own 24h cooldown so the user
      // isn't permanently locked out of a code they never received.
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "Could not issue voucher";
      if (status === 429) {
        try { localStorage.removeItem(STORAGE_KEY); } catch {}
      }
      toast.error(detail);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!voucher) return;
    try {
      await navigator.clipboard.writeText(voucher.code);
      setCopied(true);
      toast.success("Voucher copied");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Copy failed — please note the code");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="exit-intent-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-md"
          onClick={() => setOpen(false)}
          data-testid="exit-intent-modal"
        >
          <motion.div
            initial={{ scale: 0.9, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md overflow-hidden border border-velvet-gold/50 bg-velvet-coal p-8 md:p-10"
          >
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1.2, delay: 0.1 }}
              className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-velvet-rose/25 blur-3xl"
            />
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              data-testid="exit-intent-close"
              className="absolute right-4 top-4 z-10 text-white/40 hover:text-white"
            >
              <X size={16} />
            </button>

            {!voucher ? (
              <div className="relative">
                <Sparkles className="mx-auto text-velvet-gold" size={28} />
                <p className="mt-6 text-center text-xs uppercase tracking-luxe text-velvet-gold">Wait — before you go</p>
                <h2 className="mt-4 text-center font-heading text-4xl font-light leading-tight text-white md:text-5xl">
                  Here&rsquo;s <span className="italic text-velvet-rose">R100 off</span><br />your first month.
                </h2>
                <p className="mt-6 text-center text-sm font-light leading-relaxed text-white/60">
                  Sign up, claim your voucher, and unlock the full directory + inbox
                  for just <span className="text-velvet-gold">R250</span> instead of R350.
                </p>
                <button
                  onClick={claim}
                  disabled={loading}
                  data-testid="exit-intent-claim"
                  className="mt-8 w-full rounded-full bg-velvet-rose py-4 text-xs uppercase tracking-luxe text-black transition-colors hover:bg-velvet-gold disabled:opacity-60"
                >
                  {loading ? "Generating your code…" : "Claim my R100 off"}
                </button>
                <p className="mt-4 text-center text-[10px] uppercase tracking-luxe text-white/30">
                  Single-use · Expires in 14 days · No card required
                </p>
              </div>
            ) : (
              <div className="relative text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
                  className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-velvet-gold/40 bg-velvet-gold/10"
                >
                  <Sparkles className="text-velvet-gold" size={22} />
                </motion.div>
                <p className="mt-6 text-xs uppercase tracking-luxe text-velvet-gold">Your voucher</p>
                <div className="mt-4 flex items-center justify-center gap-3 border-y border-velvet-gold/30 py-6">
                  <span
                    data-testid="exit-intent-code"
                    className="font-mono text-3xl tracking-widest text-white"
                  >
                    {voucher.code}
                  </span>
                  <button
                    onClick={copy}
                    data-testid="exit-intent-copy"
                    className="rounded-full border border-white/20 p-2 text-white/60 hover:border-velvet-gold hover:text-velvet-gold"
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                </div>
                <p className="mt-3 text-sm font-light text-white/60">
                  R100 off — expires {voucher.expires_at}
                </p>
                <Link
                  to="/register"
                  onClick={() => setOpen(false)}
                  data-testid="exit-intent-signup"
                  className="mt-8 block w-full rounded-full bg-velvet-rose py-4 text-xs uppercase tracking-luxe text-black transition-colors hover:bg-velvet-gold"
                >
                  Sign up &amp; redeem
                </Link>
                <button
                  onClick={() => setOpen(false)}
                  className="mt-4 text-[10px] uppercase tracking-luxe text-white/40 hover:text-white"
                >
                  Save it for later
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
